/**
 * Alarm domain helpers (daemon `/alarms`): untrusted wire parsing, draft/form
 * validation, repeat-day logic and the pure "next occurrence" calculation used
 * by the form preview and the PWA fallback scheduler.
 *
 * `next_at` is always recomputed by the daemon and remains authoritative. The
 * PWA only previews it locally (and uses it for the unreliable in-page
 * fallback); it never treats a local calculation as a saved daemon state.
 */
import type { Alarm } from './types';
import { isValidHourMinute, parseTimeInput, timeInputValue } from './datetime';
import {
  cappedText,
  isEntityId,
  isRecord,
  normalizedTimestamp,
  optionalTimestamp,
} from './validation';

export const ALARM_LABEL_MAX_CHARS = 80;
export const ALARM_SOUND_MAX_CHARS = 64;
export const ALARM_MAX_ITEMS = 1000;
export const ALARM_DEFAULT_SOUND = 'default';

export const ALARM_SOUND_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'default', label: '默认' },
  { value: 'gentle', label: '轻柔' },
  { value: 'alert', label: '警示' },
];

export const ALARM_WEEKDAY_LABELS: Record<number, string> = {
  1: '周一',
  2: '周二',
  3: '周三',
  4: '周四',
  5: '周五',
  6: '周六',
  7: '周日',
};

/** Short labels for the weekday chips (Mon..Sun). */
export const ALARM_WEEKDAY_SHORT: ReadonlyArray<{ day: number; label: string }> = [
  { day: 1, label: '一' },
  { day: 2, label: '二' },
  { day: 3, label: '三' },
  { day: 4, label: '四' },
  { day: 5, label: '五' },
  { day: 6, label: '六' },
  { day: 7, label: '日' },
];

/** Normalize ISO weekdays: numbers/`"1"`.."7", unique, ascending. */
export function normalizeRepeatDays(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  const days = new Set<number>();
  for (const raw of value) {
    const day =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && /^[1-7]$/u.test(raw)
          ? Number(raw)
          : Number.NaN;
    if (Number.isInteger(day) && day >= 1 && day <= 7) days.add(day);
  }
  return [...days].sort((left, right) => left - right);
}

function capLabel(value: string): string {
  return cappedText(value, ALARM_LABEL_MAX_CHARS);
}

function capSound(value: string): string {
  const sound = cappedText(value, ALARM_SOUND_MAX_CHARS);
  // The daemon only accepts default/gentle/alert; unknown values fail safe to
  // the default so an old row cannot make a valid form unsubmittable.
  return isAlarmSoundKnown(sound) ? sound : ALARM_DEFAULT_SOUND;
}

export function isAlarmSoundKnown(sound: string): boolean {
  return ALARM_SOUND_OPTIONS.some((option) => option.value === sound);
}

export function alarmSoundLabel(sound: string): string {
  return ALARM_SOUND_OPTIONS.find((option) => option.value === sound)?.label ?? sound;
}

/** Parse one untrusted alarm row; invalid rows are dropped by the caller. */
export function alarmFromUnknown(value: unknown): Alarm | null {
  if (!isRecord(value)) return null;
  if (!isEntityId(value.id)) return null;
  if (value.label !== undefined && value.label !== null && typeof value.label !== 'string') {
    return null;
  }
  const label = typeof value.label === 'string' ? capLabel(value.label) : '';
  if (typeof value.hour !== 'number' || typeof value.minute !== 'number') return null;
  if (!isValidHourMinute(value.hour, value.minute)) return null;
  if (
    value.repeat_days !== undefined &&
    value.repeat_days !== null &&
    !Array.isArray(value.repeat_days)
  ) {
    return null;
  }
  const createdAt = normalizedTimestamp(value.created_at);
  const updatedAt = normalizedTimestamp(value.updated_at) || createdAt;
  return {
    id: value.id,
    label: label.length > 0 ? label : '闹钟',
    hour: value.hour,
    minute: value.minute,
    repeat_days: normalizeRepeatDays(value.repeat_days),
    // Fail safe: a missing enabled flag never creates an active alarm.
    enabled: value.enabled === true,
    next_at: optionalTimestamp(value.next_at),
    sound: typeof value.sound === 'string' ? capSound(value.sound) : ALARM_DEFAULT_SOUND,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/** Parse a `/alarms` response (`{ alarms }` or a bare array). */
export function alarmsFromUnknown(value: unknown): Alarm[] | null {
  const rawList = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.alarms)
      ? value.alarms
      : null;
  if (rawList === null) return null;
  const alarms = new Map<string, Alarm>();
  for (const raw of rawList) {
    const alarm = alarmFromUnknown(raw);
    if (alarm !== null) alarms.set(alarm.id, alarm);
  }
  return sortAlarms([...alarms.values()]).slice(0, ALARM_MAX_ITEMS);
}

export function formatAlarmTime(hour: number, minute: number): string {
  return timeInputValue(hour, minute) || '--:--';
}

/** `仅一次` / `每天` / `工作日` / `周末` / `周一、周三`. */
export function formatRepeatDays(days: readonly number[]): string {
  const normalized = normalizeRepeatDays(days);
  if (normalized.length === 0) return '仅一次';
  if (normalized.length === 7) return '每天';
  if (normalized.length === 5 && normalized.every((day) => day >= 1 && day <= 5)) {
    return '工作日';
  }
  if (normalized.length === 2 && normalized[0] === 6 && normalized[1] === 7) return '周末';
  return normalized.map((day) => ALARM_WEEKDAY_LABELS[day] ?? String(day)).join('、');
}

function isoWeekday(date: Date): number {
  const day = date.getDay();
  return day === 0 ? 7 : day;
}

/**
 * Next local occurrence strictly after `fromSeconds` (epoch seconds).
 *
 * Empty `repeat_days` = one-shot: the next occurrence of `hour:minute` (today
 * when still in the future, otherwise tomorrow). Invalid hour/minute returns
 * `null`. This is a pure preview/fallback helper; the daemon's `next_at` wins.
 */
export function nextAlarmAt(
  spec: Pick<Alarm, 'hour' | 'minute' | 'repeat_days'>,
  fromSeconds: number,
): number | null {
  if (!isValidHourMinute(spec.hour, spec.minute)) return null;
  if (!Number.isFinite(fromSeconds)) return null;
  const from = new Date(Math.floor(fromSeconds) * 1000);
  const days = normalizeRepeatDays(spec.repeat_days);
  for (let offset = 0; offset <= 7; offset += 1) {
    const candidate = new Date(
      from.getFullYear(),
      from.getMonth(),
      from.getDate() + offset,
      spec.hour,
      spec.minute,
      0,
      0,
    );
    if (days.length > 0 && !days.includes(isoWeekday(candidate))) continue;
    if (candidate.getTime() <= from.getTime()) continue;
    return Math.floor(candidate.getTime() / 1000);
  }
  return null;
}

/** `next_at` when still in the future, else the next computed occurrence. */
export function effectiveAlarmNextAt(alarm: Alarm, fromSeconds: number): number | null {
  if (alarm.next_at !== null && alarm.next_at > fromSeconds) return alarm.next_at;
  return nextAlarmAt(alarm, fromSeconds);
}

export function sortAlarms(alarms: readonly Alarm[]): Alarm[] {
  return [...alarms].sort((left, right) => {
    if (left.enabled !== right.enabled) return left.enabled ? -1 : 1;
    const leftNext = left.next_at ?? Number.POSITIVE_INFINITY;
    const rightNext = right.next_at ?? Number.POSITIVE_INFINITY;
    if (leftNext !== rightNext) return leftNext - rightNext;
    if (left.hour !== right.hour) return left.hour - right.hour;
    if (left.minute !== right.minute) return left.minute - right.minute;
    return right.updated_at - left.updated_at;
  });
}

export function upsertAlarm(alarms: readonly Alarm[], row: Alarm): Alarm[] {
  const index = alarms.findIndex((alarm) => alarm.id === row.id);
  if (index < 0) return sortAlarms([...alarms, row]);
  return sortAlarms(alarms.map((alarm) => (alarm.id === row.id ? row : alarm)));
}

export interface AlarmCounts {
  total: number;
  enabled: number;
}

export function alarmCounts(alarms: readonly Alarm[]): AlarmCounts {
  const enabled = alarms.filter((alarm) => alarm.enabled).length;
  return { total: alarms.length, enabled };
}

/** Nearest enabled alarm (by effective next time) for the page header. */
export function nextEnabledAlarm(alarms: readonly Alarm[], now: number): Alarm | null {
  let best: Alarm | null = null;
  let bestAt = Number.POSITIVE_INFINITY;
  for (const alarm of alarms) {
    if (!alarm.enabled) continue;
    const nextAt = effectiveAlarmNextAt(alarm, now);
    if (nextAt === null || nextAt >= bestAt) continue;
    best = alarm;
    bestAt = nextAt;
  }
  return best;
}

// ---------------------------------------------------------------------------
// Drafts / forms
// ---------------------------------------------------------------------------

export interface AlarmDraft {
  label: string;
  hour: number;
  minute: number;
  repeat_days: number[];
  enabled: boolean;
  sound: string;
}

export type AlarmPatch = Partial<AlarmDraft>;

export interface AlarmFormValues {
  label: string;
  /** `<input type="time">` value, "HH:mm". */
  time: string;
  repeat_days: number[];
  enabled: boolean;
  sound: string;
}

export type AlarmField = 'label' | 'time' | 'repeat_days' | 'sound';
export type AlarmFormErrors = Partial<Record<AlarmField, string>>;

export type AlarmDraftValidation = { ok: true; value: AlarmDraft } | { ok: false; message: string };

export type AlarmFormValidation =
  { ok: true; value: AlarmDraft } | { ok: false; errors: AlarmFormErrors; message: string };

export function emptyAlarmForm(): AlarmFormValues {
  return { label: '', time: '08:00', repeat_days: [], enabled: true, sound: ALARM_DEFAULT_SOUND };
}

export function alarmFormValuesFromAlarm(alarm: Alarm): AlarmFormValues {
  return {
    label: alarm.label,
    time: timeInputValue(alarm.hour, alarm.minute),
    repeat_days: [...alarm.repeat_days],
    enabled: alarm.enabled,
    sound: alarm.sound,
  };
}

/** Validate the alarm CRUD form before touching the client. */
export function validateAlarmForm(values: AlarmFormValues): AlarmFormValidation {
  const errors: AlarmFormErrors = {};
  const time = parseTimeInput(values.time);
  if (!time.ok) errors.time = '闹钟时间格式无效（HH:mm）';
  const sound = capSound(typeof values.sound === 'string' ? values.sound : '');
  const label = capLabel(typeof values.label === 'string' ? values.label : '');
  if (Object.keys(errors).length > 0) {
    return { ok: false, errors, message: '请检查闹钟设置后重试' };
  }
  return {
    ok: true,
    value: {
      label: label.length > 0 ? label : '闹钟',
      hour: time.hour,
      minute: time.minute,
      repeat_days: normalizeRepeatDays(values.repeat_days),
      enabled: values.enabled !== false,
      sound,
    },
  };
}

function draftHourMinute(
  value: Record<string, unknown>,
): { ok: true; hour: number; minute: number } | { ok: false; message: string } {
  if (
    typeof value.hour !== 'number' ||
    typeof value.minute !== 'number' ||
    !isValidHourMinute(value.hour, value.minute)
  ) {
    return { ok: false, message: '闹钟时间无效（hour 0-23 / minute 0-59）' };
  }
  return { ok: true, hour: value.hour, minute: value.minute };
}

function draftRepeatDays(
  value: unknown,
): { ok: true; days: number[] } | { ok: false; message: string } {
  if (value === undefined || value === null) return { ok: true, days: [] };
  if (!Array.isArray(value)) return { ok: false, message: '重复星期必须是数组' };
  const days = normalizeRepeatDays(value);
  if (days.length !== value.length) {
    return { ok: false, message: '重复星期必须是 1-7（周一至周日）' };
  }
  return { ok: true, days };
}

/** Runtime validation for a create payload (also used by the mock client). */
export function normalizeAlarmDraft(value: unknown): AlarmDraftValidation {
  if (!isRecord(value)) return { ok: false, message: '闹钟内容无效' };
  const time = draftHourMinute(value);
  if (!time.ok) return time;
  if (value.label !== undefined && value.label !== null && typeof value.label !== 'string') {
    return { ok: false, message: '闹钟名称必须是文本' };
  }
  if (value.sound !== undefined && value.sound !== null && typeof value.sound !== 'string') {
    return { ok: false, message: '闹钟铃声必须是文本' };
  }
  if (value.enabled !== undefined && typeof value.enabled !== 'boolean') {
    return { ok: false, message: '闹钟开关必须是布尔值' };
  }
  const repeat = draftRepeatDays(value.repeat_days);
  if (!repeat.ok) return repeat;
  const label = typeof value.label === 'string' ? capLabel(value.label) : '';
  return {
    ok: true,
    value: {
      label: label.length > 0 ? label : '闹钟',
      hour: time.hour,
      minute: time.minute,
      repeat_days: repeat.days,
      enabled: value.enabled !== false,
      sound: typeof value.sound === 'string' ? capSound(value.sound) : ALARM_DEFAULT_SOUND,
    },
  };
}

export type AlarmPatchValidation = { ok: true; value: AlarmPatch } | { ok: false; message: string };

/** Runtime validation for a PATCH body; only provided fields are returned. */
export function normalizeAlarmPatch(value: unknown): AlarmPatchValidation {
  if (!isRecord(value)) return { ok: false, message: '闹钟更新内容无效' };
  const patch: AlarmPatch = {};
  if (value.label !== undefined) {
    if (value.label !== null && typeof value.label !== 'string') {
      return { ok: false, message: '闹钟名称必须是文本' };
    }
    const label = typeof value.label === 'string' ? capLabel(value.label) : '';
    patch.label = label.length > 0 ? label : '闹钟';
  }
  if (value.hour !== undefined || value.minute !== undefined) {
    const time = draftHourMinute(value);
    if (!time.ok) return time;
    patch.hour = time.hour;
    patch.minute = time.minute;
  }
  if (value.repeat_days !== undefined) {
    const repeat = draftRepeatDays(value.repeat_days);
    if (!repeat.ok) return repeat;
    patch.repeat_days = repeat.days;
  }
  if (value.enabled !== undefined) {
    if (typeof value.enabled !== 'boolean') return { ok: false, message: '闹钟开关必须是布尔值' };
    patch.enabled = value.enabled;
  }
  if (value.sound !== undefined) {
    if (value.sound !== null && typeof value.sound !== 'string') {
      return { ok: false, message: '闹钟铃声必须是文本' };
    }
    patch.sound = typeof value.sound === 'string' ? capSound(value.sound) : ALARM_DEFAULT_SOUND;
  }
  return { ok: true, value: patch };
}

/** `POST /alarms` body (snake_case contract). */
export function alarmCreateBody(draft: AlarmDraft): Record<string, unknown> {
  return {
    label: draft.label,
    hour: draft.hour,
    minute: draft.minute,
    repeat_days: [...draft.repeat_days],
    enabled: draft.enabled,
    sound: draft.sound,
  };
}

/** `PATCH /alarms/:id` body: only fields the caller actually changed. */
export function alarmPatchBody(patch: AlarmPatch): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (patch.label !== undefined) body.label = patch.label;
  if (patch.hour !== undefined) body.hour = patch.hour;
  if (patch.minute !== undefined) body.minute = patch.minute;
  if (patch.repeat_days !== undefined) body.repeat_days = [...patch.repeat_days];
  if (patch.enabled !== undefined) body.enabled = patch.enabled;
  if (patch.sound !== undefined) body.sound = patch.sound;
  return body;
}

/** Demo-only seed for the mock client (never persisted as a real alarm). */
export function demoAlarms(now: number): Alarm[] {
  const weekday: Alarm = {
    id: 'alarm_demo_weekday',
    label: '起床',
    hour: 7,
    minute: 30,
    repeat_days: [1, 2, 3, 4, 5],
    enabled: true,
    next_at: null,
    sound: 'gentle',
    created_at: now - 7 * 86_400,
    updated_at: now - 86_400,
  };
  const daily: Alarm = {
    id: 'alarm_demo_daily',
    label: '准备休息',
    hour: 23,
    minute: 0,
    repeat_days: [1, 2, 3, 4, 5, 6, 7],
    enabled: true,
    next_at: null,
    sound: 'default',
    created_at: now - 14 * 86_400,
    updated_at: now - 2 * 86_400,
  };
  const weekend: Alarm = {
    id: 'alarm_demo_weekend',
    label: '周末跑步',
    hour: 9,
    minute: 30,
    repeat_days: [6, 7],
    enabled: false,
    next_at: null,
    sound: 'gentle',
    created_at: now - 3 * 86_400,
    updated_at: now - 3 * 86_400,
  };
  const once: Alarm = {
    id: 'alarm_demo_once',
    label: '看牙医',
    hour: 14,
    minute: 0,
    repeat_days: [],
    enabled: true,
    next_at: null,
    sound: 'alert',
    created_at: now - 3600,
    updated_at: now - 3600,
  };
  const alarms = [weekday, daily, weekend, once];
  return alarms.map((alarm) => ({
    ...alarm,
    next_at: alarm.enabled ? nextAlarmAt(alarm, now) : null,
  }));
}
